import { size, isNull } from 'lodash';
import { InvestmentStrategyType } from '@/api/enums';
import { Section, ScreenSource } from '@/enums';
import { roundToNearestN } from '@/components/utils/arithmetic';

export default {
    selectedLanguage: state => state.language,
    screenSource: state => state.screenSource,
    settings: state => {
        return {
            emailRequiredForExistingCustomer: state.settings.emailRequiredForExistingCustomer,
            emailRequiredForNewCustomer: state.settings.emailRequiredForNewCustomer,
            mobileAppUrl: state.settings.mobileAppUrl,
            onlineAppointmentSchedulerUrl: `${state.settings.onlineAppointmentSchedulerUrl}?productType=investment&lang=${state.language}&analyticsEnabled=false`,
            minInvestmentHorizon: state.settings.minInvestmentHorizon,
            maxInvestmentHorizon: state.settings.maxInvestmentHorizon,
            minInvestmentAmount: state.settings.minInvestmentAmount,
            maxInvestmentAmountViaInput: state.settings.maxInvestmentAmountViaInput,
            maxInvestmentAmountViaSlider: state.settings.maxInvestmentAmountViaSlider,
        };
    },
    minInvestmentHorizon: state => state.settings.minInvestmentHorizon,
    maxInvestmentHorizon: state => state.settings.maxInvestmentHorizon,
    minInvestmentAmount: state => state.settings.minInvestmentAmount,
    maxInvestmentAmountViaInput: state =>
        state.settings.maxInvestmentAmountViaInput,
    maxInvestmentAmountViaSlider: state =>
        state.settings.maxInvestmentAmountViaSlider,
    infoBoxTimeout: state => state.settings.infoBoxTimeout,
    iframeParams: state => state.iframeParams,
    isInEditMode: state => state.isInEditMode,
    viewport: state => state.viewport,
    viewportNoMatch: state => state.viewport && state.viewport.noMatch,
    viewportIsLarge: state => state.viewport && state.viewport.isLarge,
    selectedInvestmentStrategyAboveRecommended: state => {
        if (
            !state.investmentStrategySelected ||
      !state.investmentStrategyRecommendedBasedOnInvestmentHorizon
        ) {
            return false;
        }
        return (
            state.investmentStrategySelected.risk >
      state.investmentStrategyRecommendedBasedOnInvestmentHorizon.risk
        );
    },
    investmentAmount: state => state.investmentAmount,
    investmentHorizon: state => state.investmentHorizon,
    investmentDiagramResponse: state =>
        state.investmentDiagramResponse &&
    state.investmentDiagramResponse.map(item => {
        return {
            investmentStrategyType: item.investmentStrategyType,
            month: item.month,
            expectationLow: roundToNearestN(item.expectationLow),
            expectationAverage: roundToNearestN(item.expectationAverage),
            expectationHigh: roundToNearestN(item.expectationHigh),
        };
    }),
    investmentStrategySelected: state => state.investmentStrategySelected,
    previousInvestmentStrategySelected: state => state.previousInvestmentStrategySelected,
    investmentObjective: state => state.investmentObjective,
    investmentStrategyRecommendedBasedOnInvestmentHorizon: state =>
        state.investmentStrategyRecommendedBasedOnInvestmentHorizon,
    investmentTopic: state => state.investmentTopic,
    investmentRegion: state => state.investmentRegion,
    otherInvestments: state => state.otherInvestments,
    riskToleranceBlock1SelectedAnswer: state =>
        state.riskToleranceBlock1SelectedAnswer,
    riskToleranceBlock2SelectedAnswer: state =>
        state.riskToleranceBlock2SelectedAnswer,
    riskToleranceBlock3SelectedAnswer: state =>
        state.riskToleranceBlock3SelectedAnswer,
    investmentStrategyRecommendedBasedOnRiskTolerance: state =>
        state.investmentStrategyRecommendedBasedOnRiskTolerance,
    emailAddress: state => state.emailAddress,
    configurationData: state => {
        return {
            objective: state.investmentObjective,
            timeHorizon: state.investmentHorizon,
            amount: state.investmentAmount,
            strategyByInvestmentHorizon: state.investmentStrategyRecommendedBasedOnInvestmentHorizon && state.investmentStrategyRecommendedBasedOnInvestmentHorizon.name,
            strategySelected: state.investmentStrategySelected && state.investmentStrategySelected.name,
            topic: state.investmentTopic,
            otherInvestments: state.otherInvestments,
            region: state.investmentRegion,
            riskToleranceAnswer1: state.riskToleranceBlock1SelectedAnswer,
            riskToleranceAnswer2: state.riskToleranceBlock2SelectedAnswer,
            riskToleranceAnswer3: state.riskToleranceBlock3SelectedAnswer,
            strategyByRiskTolerance: state.investmentStrategyRecommendedBasedOnRiskTolerance
                ? state.investmentStrategyRecommendedBasedOnRiskTolerance.name : null,
            existingCustomer: state.userIsCustomer,
            language: state.language,
            email: state.emailAddress,
            screenSource: state.screenSource,
        };
    },
    determineNextSectionAfterInvestmentStrategy: state => {
        if (state.investmentStrategySelected &&
            state.investmentStrategySelected.risk >= InvestmentStrategyType.BALANCED.risk) {
            if (state.investmentStrategyRecommendedBasedOnRiskTolerance === null ||
                state.investmentStrategyRecommendedBasedOnRiskTolerance.risk < state.investmentStrategySelected.risk) {
                return Section.RISK_TOLERANCE;
            }
        }
        return Section.THEME;
    },
    editValueList: state => state.route.query && state.route.query.Valuelist,
    editConfiguration: state => state.editConfiguration,
    hasAnyValueChangeInEditMode: getters => {
        const edit = getters.editConfiguration;
        const strategyByInvestmentHorizonIsNotNull = !isNull(getters.investmentStrategyRecommendedBasedOnInvestmentHorizon);
        const strategyByRiskToleranceIsNotNull = !isNull(getters.investmentStrategyRecommendedBasedOnRiskTolerance);
        const strategySelectedIsNotNull = !isNull(getters.investmentStrategySelected);
        const hasChanged = edit && (
            edit.amount !== getters.investmentAmount ||
            edit.objective !== getters.investmentObjective ||
            edit.otherInvestments !== getters.otherInvestments ||
            edit.region !== getters.investmentRegion ||
            edit.riskToleranceAnswer1 !== getters.riskToleranceBlock1SelectedAnswer ||
            edit.riskToleranceAnswer2 !== getters.riskToleranceBlock2SelectedAnswer ||
            edit.riskToleranceAnswer3 !== getters.riskToleranceBlock3SelectedAnswer ||
            (strategyByInvestmentHorizonIsNotNull && (edit.strategyByInvestmentHorizon !== getters.investmentStrategyRecommendedBasedOnInvestmentHorizon.name)) ||
            (strategyByRiskToleranceIsNotNull && (edit.strategyByRiskTolerance !== getters.investmentStrategyRecommendedBasedOnRiskTolerance.name)) ||
            (strategySelectedIsNotNull && (edit.strategySelected !== getters.investmentStrategySelected.name)) ||
            edit.timeHorizon !== getters.investmentHorizon ||
            edit.topic !== getters.investmentTopic
        );
        return hasChanged;
    },
    isModelPortfolioAvailable: state => {
        return size(state.modelPortfolioPositions) > 0;
    },
    isInEditModeWithoutValueChanges: getters => {
        return getters.isInEditMode && !getters.hasAnyValueChangeInEditMode;
    },
    isScreenSourceWebAppMobile: state => {
        return state.screenSource === ScreenSource.WEB_APP_MOBILE;
    },
};
